'use client';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import { Anchor } from '@repo/ui/src/components/Anchor';
import ProductPrice from '@repo/ui/src/components/Product/Price';
import type { ComparisonProduct } from './types';
import { classNames } from '@repo/ui/src/utils/classNames';
import { ImageProps } from '@repo/ui/src/types';
import buildImageSrc from '@repo/ui/src/functions/image-functions-aspect-ratio';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import { useIsMD } from '@repo/ui/src/utils/useMediaQuery';
import DropdownWrapper from './_DropdownWrapper';
import Image from '@repo/ui/src/components/Image/New';
import { appConfig } from '@repo/ui/src/utils/appConfig';

interface Props {
  selectedSkus: Array<string | ''>;
  selectableSkus: Array<string>;
  products: Array<ComparisonProduct>;
  _testID: string;
  updateSelectedProducts: (sku: string, index: number) => void;
  noSelectionImage?: ImageProps;
  asContentRow?: boolean;
  vip?: boolean;
  onProductClick?: (product: ComparisonProduct, position: number) => void;
}

const ProductActions = ({
  selectedSkus,
  selectableSkus,
  products,
  updateSelectedProducts,
  _testID,
  noSelectionImage,
  asContentRow,
  vip,
  onProductClick,
}: Props) => {
  const { t } = useTranslation();
  const placeholderImage = buildImageSrc(noSelectionImage, 200, 1, 1);
  const mdAndUp = useIsMD();

  const selectableProducts = selectableSkus
    .map((sku) => products.find((product) => product.sku === sku))
    .filter(Boolean) as Array<ComparisonProduct>;

  return (
    <>
      <DropdownWrapper
        selectableProducts={selectableProducts}
        selectedSkus={selectedSkus}
        asContentRow={asContentRow}
        updateSelectedProducts={updateSelectedProducts}
      />
      <div className="relative z-20" data-test-id={`product_actions_${_testID}`}>
        <div className="relative w-full">
          <div
            className={classNames(
              'grid bg-white px-6 pt-2 gap-6 pb-10 w-full',
              asContentRow ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-4',
            )}
            style={
              mdAndUp && asContentRow
                ? {
                    gridTemplateColumns: `repeat(${selectedSkus.length + 1}, minmax(0, 1fr))`,
                  }
                : undefined
            }
          >
            {mdAndUp && <div></div>}
            {selectedSkus.map((sku, i) => {
              const product = products.find(
                ({ sku: productSku }: { sku: string }) => sku === productSku,
              );
              return (
                <div
                  className="flex flex-col text-center h-full justify-between"
                  key={i}
                  role="group"
                  aria-label="Product comparison item"
                >
                  {asContentRow && mdAndUp && (
                    <h4 className="tra-text-web-heading-6">{product?.title}</h4>
                  )}
                  <div className="relative">
                    <div className="md:p-5 flex center" key={i}>
                      {product?.mainImageUrl ? (
                        <Image
                          className="w-full h-full object-contain aspect-square max-h-[280px]"
                          src={product.mainImageUrl}
                          alt=""
                          width={200}
                          height={200}
                        />
                      ) : (
                        <img
                          className="w-full h-full object-contain aspect-square max-h-[280px]"
                          src={placeholderImage}
                          alt=""
                        />
                      )}
                    </div>
                    {!!product?.price && (
                      <ProductPrice
                        product={product}
                        align="center"
                        variant="sub"
                        flipOrder
                        className="pb-3 items-center"
                        strikethroughClassName={appConfig.isMeater ? 'text-neutral-500' : ''}
                        vip={vip}
                      />
                    )}
                    <div>
                      <div className="flex flex-wrap justify-center items-center gap-5">
                        {product?.productUrl && (
                          <Button
                            as={Anchor}
                            link={product?.productUrl}
                            className="!no-underline !px-2 xl:!px-6"
                            size="small"
                            variant={asContentRow ? ButtonVariant.skewer : ButtonVariant.secondary}
                            onClick={() => onProductClick?.(product, i)}
                          >
                            {t('productComparisonContent.viewDetailsLabel', undefined, {
                              default: 'Shop Now',
                            })}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                  {!mdAndUp && asContentRow && (
                    <h4 className="tra-text-web-heading-4 !text-[32px]">{product?.title}</h4>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductActions;
